import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, Square, CheckSquare, XCircle, ListChecks } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ColorImage } from '../fabric/ColorImage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Catalog colors without an AI room + a background generation queue (one image per color, sequential).
export const MissingRoomsPanel = ({ token, productType, roomTypes, styleOptions, onGenerated }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [roomType, setRoomType] = useState(roomTypes[0]);
  const [style, setStyle] = useState(styleOptions[0]);
  const [queue, setQueue] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/visualizations/missing?product_type=${productType}`, { headers });
      setMissing(data.colors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setSelected(new Set()); }, [productType]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollQueue = () => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const { data } = await axios.get(`${API_URL}/admin/visualizations/generate-queue/status`, { headers });
      setQueue(data);
      if (!data.running) {
        clearInterval(timer.current);
        toast.success(`Queue finished: ${data.done - data.failed} generated, ${data.failed} failed`);
        load();
        onGenerated?.();
      }
    }, 3000);
  };
  useEffect(() => {
    axios.get(`${API_URL}/admin/visualizations/generate-queue/status`, { headers }).then(({ data }) => { if (data.running) { setQueue(data); pollQueue(); } }).catch(() => {});
    return () => clearInterval(timer.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return missing.filter(c => !q || [c.color_name, c.color_code, c.material_name, c.collection_name, c.brand_name].some(x => (x || '').toLowerCase().includes(q)));
  }, [missing, filter]);

  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllVisible = () => setSelected(new Set(rows.map(r => r.color_id)));

  const startQueue = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Generate ${selected.size} AI room image(s)? Each image uses AI credits.`)) return;
    try {
      const { data } = await axios.post(`${API_URL}/admin/visualizations/generate-queue`, { color_ids: [...selected], product_type: productType, room_type: roomType, style }, { headers });
      if (!data.started) { toast.info('A generation queue is already running'); }
      else toast.success(`Queued ${data.total} colors for generation`);
      setSelected(new Set());
      setQueue({ running: true, total: data.total, done: 0, failed: 0 });
      pollQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start queue');
    }
  };

  const cancelQueue = async () => {
    const { data } = await axios.post(`${API_URL}/admin/visualizations/generate-queue/cancel`, {}, { headers });
    toast.info(`Removed ${data.cancelled} pending colors from the queue`);
  };

  return (
    <div className="space-y-4" data-testid="missing-rooms-panel">
      {queue?.running && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 flex items-center gap-3" data-testid="gen-queue-banner">
          <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
          <div className="flex-1 text-xs">
            <p className="font-semibold text-neutral-800">Generating AI rooms — {queue.done}/{queue.total}{queue.failed ? ` (${queue.failed} failed)` : ''}</p>
            <p className="text-neutral-600 truncate">{queue.current ? `Now: ${queue.current}` : 'Starting…'}</p>
            <div className="mt-1 h-1 bg-white rounded-full overflow-hidden"><div className="h-full bg-sky-500 transition-all" style={{ width: `${queue.total ? Math.round(queue.done / queue.total * 100) : 0}%` }} /></div>
          </div>
          <Button size="sm" variant="outline" onClick={cancelQueue} className="h-7 text-xs" data-testid="gen-queue-cancel"><XCircle className="w-3 h-3 mr-1" /> Stop after current</Button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter by color, code, material, collection…" className="h-8 text-xs w-72" data-testid="missing-filter" />
        <Button size="sm" variant="outline" onClick={selectAllVisible} className="h-8 text-xs" data-testid="missing-select-all"><ListChecks className="w-3.5 h-3.5 mr-1" /> Select visible ({rows.length})</Button>
        {selected.size > 0 && <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-8 text-xs">Clear selection</Button>}
        <div className="ml-auto flex items-center gap-2">
          <select value={roomType} onChange={e => setRoomType(e.target.value)} className="border rounded-md h-8 px-2 text-xs" data-testid="missing-room-type">{roomTypes.map(r => <option key={r} value={r}>{r}</option>)}</select>
          <select value={style} onChange={e => setStyle(e.target.value)} className="border rounded-md h-8 px-2 text-xs" data-testid="missing-style">{styleOptions.map(r => <option key={r} value={r}>{r}</option>)}</select>
          <Button size="sm" onClick={startQueue} disabled={!selected.size || queue?.running} className="h-8 text-xs bg-amber-600 hover:bg-amber-700" data-testid="missing-generate-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Queue {selected.size || ''} for generation
          </Button>
        </div>
      </div>
      <p className="text-xs text-neutral-500" data-testid="missing-count">{missing.length} catalog colors have no AI room for this product type. Only real catalog colors (with article codes where the brand publishes them) can be queued.</p>
      {loading ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 max-h-[60vh] overflow-y-auto pr-1">
          {rows.map(c => {
            const on = selected.has(c.color_id);
            return (
              <button key={`${c.collection_id}:${c.material_id}:${c.color_id}`} type="button" onClick={() => toggle(c.color_id)} className={`text-left rounded-lg border p-2 flex gap-2 items-center transition-colors ${on ? 'border-amber-500 bg-amber-50' : 'border-neutral-200 bg-white hover:border-amber-300'}`} data-testid={`missing-color-${c.color_id}`}>
                <div className="w-10 h-10 rounded overflow-hidden border flex-shrink-0 bg-neutral-100">
                  {c.image_url ? <ColorImage src={c.image_url} alt="" w={128} className="w-full h-full" /> : <div className="w-full h-full" style={{ backgroundColor: c.color_hex || '#ddd' }} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{c.color_name}</p>
                  <p className="text-[10px] text-neutral-500 truncate">{c.color_code || c.collection_name}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{[c.brand_name, c.material_name || c.collection_name].filter(Boolean).join(' › ')}</p>
                </div>
                {on ? <CheckSquare className="w-4 h-4 text-amber-600 flex-shrink-0" /> : <Square className="w-4 h-4 text-neutral-300 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MissingRoomsPanel;
